import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  createBillingSetupIntent,
  createCheckoutSession,
  createPortalSession,
  fetchSubscriptionState,
  startTrial,
  submitEnterpriseContact,
  toggleComplianceAddon,
} from "@/services/billing";
import type {
  AddonToggleRequest,
  AddonToggleResponse,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  EnterpriseContactRequest,
  EnterpriseContactResponse,
  PortalSessionResponse,
  SetupIntentResponse,
  StartTrialRequest,
  StartTrialResponse,
  SubscriptionState,
} from "@/types";
import { WORKSPACE_ADDONS_STATE_KEY } from "@/hooks/useWorkspaceAddons";
import { AUTH_SESSION_QUERY_KEY } from "@/lib/authContextValue";

/**
 * TanStack Query hooks for the /api/v1/billing/* surface.
 *
 * Source: ENG-BILLING-STRIPE frontend half.
 *
 * Server-state caching key for the subscription read so toggling an
 * add-on or starting a Checkout flow can invalidate it.
 */

export const SUBSCRIPTION_QUERY_KEY = ["billing", "subscription"] as const;

export function useSubscriptionState(): UseQueryResult<
  SubscriptionState,
  Error
> {
  return useQuery<SubscriptionState, Error>({
    queryKey: SUBSCRIPTION_QUERY_KEY,
    queryFn: fetchSubscriptionState,
    staleTime: 30_000, // 30s — subscription state mutates on webhook
  });
}

export function useCreateCheckoutSession() {
  return useMutation<CheckoutSessionResponse, Error, CheckoutSessionRequest>({
    mutationFn: createCheckoutSession,
  });
}

export function useCreatePortalSession() {
  return useMutation<PortalSessionResponse, Error, void>({
    mutationFn: createPortalSession,
  });
}

/**
 * Canonical Compliance-add-on toggle — `POST /api/v1/billing/addons/compliance`
 * (ENG-TRUE-DEDUP-UX-POLISH dual-path unification 2026-07-12; the workspace
 * PUT wrapper + its `useToggleComplianceAddon` in `useWorkspaceAddons.ts` are
 * DELETED). The backend now also flips `Workspace.compliance_addon_active`
 * and emits the `compliance_addon_toggled` audit event, so on success we
 * invalidate the Add-ons panel state read + the auth session alongside the
 * subscription read.
 */
export function useToggleComplianceAddon() {
  const queryClient = useQueryClient();
  return useMutation<AddonToggleResponse, Error, AddonToggleRequest>({
    mutationFn: toggleComplianceAddon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: WORKSPACE_ADDONS_STATE_KEY });
      queryClient.invalidateQueries({ queryKey: AUTH_SESSION_QUERY_KEY });
    },
  });
}

/**
 * Onboarding Step 1 — Stripe Elements direct-API flow.
 *
 * Source: ENG-BILLING-STRIPE Phase 1 Sub-B (backend) + Sub-D (frontend).
 *
 * Invalidates ``SUBSCRIPTION_QUERY_KEY`` on success so the Settings →
 * Billing page reflects the new trial immediately + the Workspace's
 * downstream ``signup_state == 'trial_active'`` gate flips for
 * paywall-gated UI.
 */
export function useStartTrial() {
  const queryClient = useQueryClient();
  return useMutation<StartTrialResponse, Error, StartTrialRequest>({
    mutationFn: startTrial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
    },
  });
}

/**
 * Onboarding Step 1 — Enterprise sales-lead flow.
 *
 * Source: ENG-BILLING-STRIPE Phase 1 Sub-C (backend) + Sub-D (frontend).
 *
 * Invalidates ``SUBSCRIPTION_QUERY_KEY`` on success so any downstream
 * surface that reads ``signup_state`` reflects the
 * ``'trial_pending_sales'`` flip without a manual refetch.
 */
export function useSubmitEnterpriseContact() {
  const queryClient = useQueryClient();
  return useMutation<
    EnterpriseContactResponse,
    Error,
    EnterpriseContactRequest
  >({
    mutationFn: submitEnterpriseContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
    },
  });
}

/**
 * Onboarding Step 1 — Stripe SetupIntent client_secret issuance.
 *
 * Source: ENG-BILLING-STRIPE Phase 1 Sub-D (backend + frontend).
 *
 * Mutation rather than query because the SetupIntent client_secret is
 * single-use — a fresh fetch is required after each retry. No cache
 * invalidation: the SetupIntent issuance does not mutate any tracked
 * server state (signup_state only flips on Subscription create).
 */
export function useCreateBillingSetupIntent() {
  return useMutation<SetupIntentResponse, Error, void>({
    mutationFn: createBillingSetupIntent,
  });
}
